<template>
  <div id="reportPage">
    <main>
      <article class="settings-card">
        <div class="title-wrapper mb-4">
          <h2 class="login-title">SETTINGS</h2>
          <UserMenu :roles="roles" :user-name="userName" />
        </div>

        <div class="settings-section">
          <div class="settings-section-title">Billable Auto Rules (global default)</div>
          <p class="settings-section-text">
            Default for all clients: calls without a manual override are treated as billable when connected duration is greater than this threshold. Individual clients can override this below.
          </p>

          <div class="settings-form-row">
            <label class="settings-label" for="billable-threshold">Global billable threshold, seconds</label>
            <input
              id="billable-threshold"
              v-model.number="globalThresholdSeconds"
              type="number"
              min="1"
              class="settings-input"
            />
          </div>

          <div class="settings-actions">
            <button class="btn btn-primary" :disabled="isSaving" @click="saveGlobalSettings">
              <span v-if="isSaving">Saving...</span>
              <span v-else>Save global default</span>
            </button>
          </div>

          <div v-if="savedMessage" class="settings-message success">{{ savedMessage }}</div>
          <div v-if="errorMessage" class="settings-message error">{{ errorMessage }}</div>
        </div>

        <div class="settings-section settings-section-spaced">
          <div class="settings-section-title">Per-client threshold</div>
          <p class="settings-section-text">
            Choose a client and set a custom threshold in seconds, or clear the field and save to use the global default for that client.
          </p>

          <div class="settings-form-row settings-form-row-wide">
            <label class="settings-label" for="client-select">Client</label>
            <Multiselect
              id="client-select"
              v-model="selectedClientUsername"
              :options="clientOptionRows"
              value-prop="user"
              label="label"
              :track-by="clientMultiselectTrackBy"
              :searchable="true"
              :allow-empty="true"
              :append-to-body="false"
              placeholder="Search by login or company..."
              no-results-text="No matching clients"
              class="settings-multiselect"
            />
          </div>

          <div v-if="selectedClientUsername" class="settings-form-row">
            <label class="settings-label" for="client-threshold">Custom threshold (seconds)</label>
            <input
              id="client-threshold"
              v-model="clientThresholdInput"
              type="number"
              min="1"
              class="settings-input"
              placeholder="Empty = use global default"
            />
            <p class="settings-hint">
              Effective for this client now: <strong>{{ clientEffectiveSeconds }}</strong> s
              <span v-if="clientUserOverrideDisplay !== null"> (override: {{ clientUserOverrideDisplay }} s)</span>
              <span v-else> (using global)</span>
            </p>
          </div>

          <div v-if="selectedClientUsername" class="settings-actions settings-actions-row">
            <button
              class="btn btn-primary"
              :disabled="isSavingClient"
              @click="saveClientOverride"
            >
              <span v-if="isSavingClient">Saving...</span>
              <span v-else>Save for this client</span>
            </button>
            <button
              type="button"
              class="btn btn-outline-secondary"
              :disabled="isSavingClient"
              @click="clearClientOverride"
            >
              Clear override (use global)
            </button>
          </div>

          <div v-if="clientSavedMessage" class="settings-message success">{{ clientSavedMessage }}</div>
          <div v-if="clientErrorMessage" class="settings-message error">{{ clientErrorMessage }}</div>
        </div>
      </article>
    </main>
  </div>
</template>

<script>
import UserMenu from '@/components/UserMenu.vue'

export default {
  components: { UserMenu },
  data() {
    return {
      userName: localStorage.getItem("username") || "User",
      roles: [],
      globalThresholdSeconds: 60,
      isSaving: false,
      savedMessage: "",
      errorMessage: "",
      clientOptionRows: [],
      clientMultiselectTrackBy: ['user', 'company_name', 'label'],
      selectedClientUsername: null,
      clientThresholdInput: "",
      clientEffectiveSeconds: "—",
      clientUserOverrideDisplay: null,
      isSavingClient: false,
      clientSavedMessage: "",
      clientErrorMessage: ""
    };
  },
  async mounted() {
    const token = localStorage.getItem('token');
    if (!token) {
      this.$router.push('/login');
      return;
    }

    await this.check_role();
    if (!this.roles.includes('admin')) {
      this.$router.push('/');
      return;
    }

    await this.fetchSettings();
    await this.loadClients();
    document.title = "Settings";
  },
  watch: {
    selectedClientUsername() {
      this.onClientSelected();
    }
  },
  methods: {
    async check_role() {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ user: this.userName });
      const res = await fetch(`/api/role?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      this.roles = data.roles || [];
      return this.roles;
    },
    async fetchSettings() {
      this.savedMessage = "";
      this.errorMessage = "";
      const token = localStorage.getItem('token');
      const res = await fetch('/api/settings/billable', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        this.errorMessage = "Failed to load settings";
        return;
      }

      const data = await res.json();
      const g = Number(data.globalThresholdSeconds ?? data.thresholdSeconds ?? 60);
      this.globalThresholdSeconds = g;
    },
    async loadClients() {
      const token = localStorage.getItem('token');
      try {
        const res = await fetch('/api/clients', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        const list = [...items].sort((a, b) =>
          String(a.user).localeCompare(String(b.user))
        );
        this.clientOptionRows = list.map((c) => {
          const company = (c.company_name || '').trim();
          return {
            user: c.user,
            company_name: company,
            label: company ? `${c.user} — ${company}` : String(c.user)
          };
        });
      } catch {
        this.clientOptionRows = [];
      }
    },
    async onClientSelected() {
      this.clientSavedMessage = "";
      this.clientErrorMessage = "";
      if (this.selectedClientUsername == null || this.selectedClientUsername === '') {
        this.clientThresholdInput = "";
        this.clientEffectiveSeconds = "—";
        this.clientUserOverrideDisplay = null;
        return;
      }
      const token = localStorage.getItem('token');
      try {
        const params = new URLSearchParams({ username: this.selectedClientUsername });
        const res = await fetch(`/api/settings/billable?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          this.clientErrorMessage = "Failed to load client settings";
          return;
        }
        const data = await res.json();
        const ov = data.userOverrideSeconds;
        this.clientUserOverrideDisplay = ov != null && ov !== '' ? Number(ov) : null;
        this.clientThresholdInput =
          this.clientUserOverrideDisplay != null ? String(this.clientUserOverrideDisplay) : '';
        this.clientEffectiveSeconds = String(
          Number(data.thresholdSeconds ?? this.globalThresholdSeconds ?? 60)
        );
      } catch (e) {
        this.clientErrorMessage = e.message || "Failed to load client settings";
      }
    },
    async saveGlobalSettings() {
      this.savedMessage = "";
      this.errorMessage = "";

      if (!this.globalThresholdSeconds || Number(this.globalThresholdSeconds) < 1) {
        this.errorMessage = "Threshold must be greater than 0";
        return;
      }

      this.isSaving = true;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/settings/billable', {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            thresholdSeconds: Number(this.globalThresholdSeconds)
          })
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to save settings');
        }

        const data = await res.json();
        this.globalThresholdSeconds = Number(data.globalThresholdSeconds ?? data.thresholdSeconds ?? this.globalThresholdSeconds);
        this.savedMessage = "Global default saved";
        if (this.selectedClientUsername != null && this.selectedClientUsername !== '') {
          await this.onClientSelected();
        }
      } catch (e) {
        this.errorMessage = e.message || "Failed to save settings";
      } finally {
        this.isSaving = false;
      }
    },
    async saveClientOverride() {
      this.clientSavedMessage = "";
      this.clientErrorMessage = "";
      if (this.selectedClientUsername == null || this.selectedClientUsername === '') {
        this.clientErrorMessage = "Select a client";
        return;
      }
      const raw = String(this.clientThresholdInput || '').trim();
      let payload;
      if (raw === '') {
        payload = { username: this.selectedClientUsername, thresholdSeconds: null };
      } else {
        const n = Number(raw);
        if (!Number.isFinite(n) || n < 1) {
          this.clientErrorMessage = "Threshold must be greater than 0";
          return;
        }
        payload = { username: this.selectedClientUsername, thresholdSeconds: n };
      }
      this.isSavingClient = true;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/settings/billable', {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || 'Failed to save');
        }
        const data = await res.json();
        const ov = data.userOverrideSeconds;
        this.clientUserOverrideDisplay = ov != null && ov !== '' ? Number(ov) : null;
        this.clientThresholdInput =
          this.clientUserOverrideDisplay != null ? String(this.clientUserOverrideDisplay) : '';
        this.clientEffectiveSeconds = String(
          Number(data.thresholdSeconds ?? this.globalThresholdSeconds ?? 60)
        );
        this.clientSavedMessage = "Client setting saved";
      } catch (e) {
        this.clientErrorMessage = e.message || "Failed to save";
      } finally {
        this.isSavingClient = false;
      }
    },
    async clearClientOverride() {
      this.clientThresholdInput = '';
      await this.saveClientOverride();
    }
  }
};
</script>

<style scoped>
#reportPage {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
  padding: 20px 10px;
}

#reportPage main {
  display: flex;
  justify-content: center;
  width: 100%;
}

.settings-card {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: 960px;
}

.title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.login-title {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #4e54c8;
}

.settings-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
}

.settings-section-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
}

.settings-section-text {
  margin: 0 0 18px;
  color: #475569;
  font-size: 14px;
}

.settings-section-spaced {
  margin-top: 24px;
}

.settings-form-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 260px;
}

.settings-form-row-wide {
  max-width: 420px;
}

.settings-multiselect {
  max-width: 420px;
}

.settings-multiselect :deep(.multiselect) {
  min-height: 40px;
  border-radius: 8px;
  border-color: #cbd5e1;
  /* Default theme uses 10rem (~4 rows); taller list with scroll */
  --ms-max-height: min(24rem, 65vh);
}

.settings-multiselect :deep(.multiselect-wrapper) {
  min-height: 40px;
}

.settings-hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: #64748b;
}

.settings-actions-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.btn-outline-secondary {
  background: #fff;
  color: #334155;
  border: 1px solid #cbd5e1;
}

.btn-outline-secondary:hover {
  background: #f1f5f9;
}

.settings-label {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.settings-input {
  height: 40px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
}

.settings-actions {
  margin-top: 18px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
}

.btn-primary {
  background: #4f46e5;
  color: #fff;
}

.settings-message {
  margin-top: 14px;
  font-size: 14px;
  font-weight: 600;
}

.settings-message.success {
  color: #15803d;
}

.settings-message.error {
  color: #b91c1c;
}
</style>
